import api from './api';

export interface News {
    id: number;
    sourceId: number;
    sourceName?: string;
    title: string;
    summary: string;
    content?: string;
    originalUrl: string;
    originalLanguage?: string;
    publishedAt: string;
    fetchedAt: string;
    sentiment?: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
    sentimentScore?: number;
    sentimentReason?: string;
    analyzedAt?: string;
    marketTags?: string;
    categoryTags?: string;
    viewCount: number;
    clickCount: number;
    guid: string;
    // 翻译内容
    titleTranslated?: string;
    summaryTranslated?: string;
    hasTranslation?: boolean;
    // 重要性: 1-一般, 2-重要, 3-无关
    importance?: number;
    source?: {
        id: number;
        name: string;
        category: string;
    };
}

export interface NewsTranslation {
    id: number;
    newsId: number;
    targetLanguage: string;
    titleTranslated: string;
    summaryTranslated: string;
    createdAt: string;
}

export interface NewsAnalysis {
    id: number;
    newsId: number;
    userId: number;
    affectedMarkets?: string;
    affectedSymbols?: string;
    tradingOpportunity?: 'LONG' | 'SHORT' | 'NEUTRAL' | 'WAIT';
    opportunityScore?: number;
    confidence?: number;
    analysisSummary?: string;
    actionSuggestion?: string;
    riskWarning?: string;
    keyFactors?: string;
    modelUsed?: string;
    tokensUsed?: number;
    analysisDuration?: number;
    createdAt: string;
}

export interface SentimentStats {
    positive: number;
    negative: number;
    neutral: number;
}

export interface NewsFilter {
    category?: 'CRYPTO' | 'FOREX' | 'MACRO' | 'STOCK';
    sentiment?: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
    keyword?: string;
    timeRange?: string;
    page?: number;
    size?: number;
}

export const newsService = {
    /**
     * 获取新闻列表
     */
    getNewsList: async (filter: NewsFilter = {}) => {
        const params = new URLSearchParams();
        if (filter.category) params.append('category', filter.category);
        if (filter.sentiment) params.append('sentiment', filter.sentiment);
        if (filter.keyword) params.append('keyword', filter.keyword);
        if (filter.timeRange) params.append('timeRange', filter.timeRange);
        params.append('page', String(filter.page ?? 0));
        params.append('size', String(filter.size ?? 12));

        const response = await api.get(`/api/v1/news?${params.toString()}`);
        return response.data.data;
    },

    /**
     * 获取情绪统计
     * @param timeRange 时间范围: 'today', 'week', 'all'
     */
    getSentimentStats: async (timeRange: string = 'week'): Promise<SentimentStats> => {
        const daysMap: Record<string, number> = {
            'today': 1,
            'yesterday': 1,
            'week': 7,
            'month': 30,
            '3day': 3,
            '7day': 7
        };
        const days = daysMap[timeRange] ?? 7;
        const response = await api.get(`/api/v1/news/stats/sentiment?days=${days}`);
        return response.data.data;
    },

    /**
     * 分析新闻交易机会
     */
    analyzeNews: async (newsId: number): Promise<NewsAnalysis> => {
        const response = await api.post(`/api/v1/news/${newsId}/analyze`, {}, {timeout: 300000});
        return response.data.data;
    },

    /**
     * 获取新闻分析结果
     */
    getAnalysis: async (newsId: number): Promise<NewsAnalysis | null> => {
        const response = await api.get(`/api/v1/news/${newsId}/analysis`);
        return response.data.data;
    },

    /**
     * 手动触发指定RSS源的抓取
     */
    fetchSourceById: async (sourceId: number): Promise<string> => {
        const response = await api.post(`/api/v1/news/fetch/${sourceId}`);
        return response.data.data;
    },

    /**
     * 获取指定RSS源的新闻列表
     */
    getNewsBySource: async (sourceId: number, page: number = 0, size: number = 20) => {
        const response = await api.get(`/api/v1/news/source/${sourceId}?page=${page}&size=${size}`);
        return response.data.data;
    },

    /**
     * 翻译新闻
     */
    translateNews: async (newsId: number, targetLanguage: string = 'zh'): Promise<NewsTranslation> => {
        const response = await api.post(`/api/v1/news/${newsId}/translate?targetLanguage=${targetLanguage}`);
        return response.data.data;
    },

    /**
     * 获取新闻翻译
     */
    getTranslation: async (newsId: number, targetLanguage: string = 'zh'): Promise<NewsTranslation | null> => {
        const response = await api.get(`/api/v1/news/${newsId}/translation?targetLanguage=${targetLanguage}`);
        return response.data.data;
    },

    /**
     * 更新新闻重要性
     * @param newsId 新闻ID
     * @param importance 重要性: 1-一般, 2-重要, 3-无关
     */
    updateImportance: async (newsId: number, importance: number): Promise<News> => {
        const response = await api.put(`/api/v1/news/${newsId}/importance?importance=${importance}`);
        return response.data.data;
    },

    /**
     * 删除新闻（逻辑删除）
     * @param newsId 新闻ID
     */
    deleteNews: async (newsId: number): Promise<void> => {
        const response = await api.delete(`/api/v1/news/${newsId}`);
        if (!response.data?.success && response.data?.code !== '0') {
            throw new Error(response.data?.message || '删除失败');
        }
    },
};
